/**
 * Telling "the session is over" apart from "we could not ask".
 *
 * A token refresh fails for two opposite reasons that used to arrive through the same `catch`:
 * Identity refused the grant, or the request never left the device. Only the first is a sign-out.
 * Treating the second as one is how a two-second drop in cellular signal threw a volunteer back to
 * the Identity login page in the middle of a check-in line.
 */

/** A response from Identity's token endpoint that was not a success, carrying its status. */
export class TokenEndpointError extends Error {
  /**
   * Initializes a new instance of the {@link TokenEndpointError} class.
   *
   * @param status The HTTP status Identity answered with.
   */
  constructor(readonly status: number) {
    super(`Token endpoint returned ${status}.`);
    this.name = 'TokenEndpointError';
  }
}

/**
 * Whether a failed refresh means the local session should be discarded.
 *
 * Only a refusal from Identity itself counts. OAuth 2.0 answers an expired or revoked refresh token
 * with `400 invalid_grant`, so the 4xx family is the definite "sign in again" signal. Everything
 * else — a 5xx while Identity is deploying, a `TypeError` from `fetch` when the signal dropped, or
 * anything unrecognized — says nothing about whether the refresh token is still good, so the
 * session is kept and the caller simply has no token this time round.
 *
 * @param error Whatever the refresh attempt threw.
 * @returns True only when Identity definitively refused.
 */
export function shouldEndSession(error: unknown): boolean {
  return error instanceof TokenEndpointError && error.status >= 400 && error.status < 500;
}
